<template>
  <div class="tw-p-6">
    <div class="tw-flex tw-items-center tw-gap-3 tw-justify-between tw-pr-8">
      <div>
        <div class="d-flex align-items-center justify-content-center tw-mb-3">
          <Typography variant="headline-5"> Notes </Typography>
        </div>
      </div>
      <div>
        <div class="tw-flex tw-items-center tw-gap-3">
          <IconButton
            v-if="showSelection"
            class="tw-w-10 tw-h-10 tw-items-center tw-justify-center tw-flex"
            @click="selectPrev"
            ><v-icon>mdi-chevron-left</v-icon></IconButton
          >
          <IconButton
            v-if="showSelection"
            class="tw-w-10 tw-h-10 tw-items-center tw-justify-center tw-flex"
            @click="selectNext"
            ><v-icon>mdi-chevron-right</v-icon></IconButton
          >
        </div>
      </div>
    </div>
    <div
      class="tw-my-3 tw-gap-3 tw-border tw-border-solid tw-border-gray-400 tw-rounded-lg tw-p-3 tw-flex tw-items-center"
    >
      <div>
        <div class="tw-w-[72px] tw-h-[72px]">
          <CustomImage
            :src="avatarUrl"
            class="tw-w-full tw-h-full tw-object-cover tw-rounded-full tw-overflow-hidden"
            :emptySrc="require('@/assets/img/empty_avatar.png')"
            width="72"
            height="72"
          />
        </div>
      </div>
      <div>
        <Typography variant="headline-6">{{
          currentCandidate?.name
        }}</Typography>
        <Typography variant="body-1" class="tw-mt-1">
          {{ candidateCurrentTitle }}
        </Typography>
        <Typography variant="body-2">
          {{ candidateLocation }}
        </Typography>
      </div>
    </div>

    <Note
      v-if="candidates[index]"
      :value="candidates[index].notes"
      :name="candidates[index].name"
    />
    <div class="btn-container text-center pt-4">
      <v-btn
        :loading="sending"
        dark
        class="text-initial"
        :class="{ disabled: isEmptyNote }"
        color="primary"
        @click="saveNotes"
        >Save Note</v-btn
      >
    </div>
    <v-snackbar v-model="snackbar" color="primaryDark" class="snackbar-text">
      {{ text }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" dark text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Note from '@/components/note/';
import Config from '@/js/config';
import { put } from '@/js/api/sourcer';
import Typography from '@/components/v3/base/Typography';
import CustomImage from '@/components/card/CustomImage';
import DefaultAvatar from '@/assets/img/empty_avatar.png';
import IconButton from '@/components/v3/base/IconButton';

export default {
  components: {
    Note,
    Typography,
    CustomImage,
    IconButton,
  },

  props: { candidates: Array, projectId: String },

  data() {
    return {
      index: 0,
      sending: false,
      snackbar: false,
      text: 'Note saved',
    };
  },

  computed: {
    showSelection() {
      return this.getCount > 1;
    },

    currentCandidate() {
      if (!this.candidates) {
        return null;
      }

      return this.candidates[this.getIndex()];
    },

    getCount() {
      return this.candidates ? this.candidates.length : 0;
    },

    isEmptyNote() {
      return !this.currentCandidate.notes;
    },

    avatarUrl() {
      if (this.currentCandidate) {
        return (
          this.currentCandidate.picture_url ||
          this.currentCandidate.profile_pic_url ||
          DefaultAvatar
        );
      }
      return DefaultAvatar;
    },

    candidateCurrentTitle() {
      if (
        !this.currentCandidate.experiences ||
        !this.currentCandidate.experiences.length
      )
        return '';
      return `${this.currentCandidate.experiences[0].title} at ${this.currentCandidate.experiences[0].company}`;
    },

    candidateLocation() {
      if (this.currentCandidate.location) return this.currentCandidate.location;
      let location = '';
      if (this.currentCandidate.city)
        location += `${this.currentCandidate.city}, `;
      if (this.currentCandidate.state) location += this.currentCandidate.state;
      else location += this.currentCandidate.country;
      return location;
    },
  },

  created() {
    this.$store.dispatch('projects/settings/loadSettings', {
      projectId: this.projectId,
    });
  },

  methods: {
    async saveNotes() {
      if (!this.currentCandidate.notes || !this.currentCandidate.notes.trim())
        return;

      this.sending = true;
      this.$amplitude_client.logEvent({
        event_type: 'save_note_fe',
        user_id: localStorage.getItem('email'),
        event_properties: {
          candidate_id: this.currentCandidate._id,
          notes: this.currentCandidate.notes,
        },
      });

      try {
        // TODO(jeremiah): clean this up after v2 rollout
        if (this.$store.getters['user/getIsUserV2']) {
          await put(`${Config.env.API_URL_V2}projects/candidates/hm_note`, {
            project_id: this.projectId,
            candidate_id: this.currentCandidate.core_candidate_id,
            note: this.currentCandidate.notes,
          });
        } else {
          await put(`${Config.env.API_URL}candidates`, {
            // eslint-disable-next-line no-underscore-dangle
            candidate_id: this.currentCandidate._id,
            notes: this.currentCandidate.notes,
          });
        }

        this.snackbar = true;
      } catch (error) {
        console.error(error.response);
      } finally {
        this.sending = false;
      }
    },

    selectPrev() {
      this.index -= 1;
      this.getIndex();
    },

    selectNext() {
      this.index += 1;
      this.getIndex();
    },

    getIndex() {
      if (this.index < 0) {
        this.index = 0;
      }
      if (this.index >= this.getCount) {
        this.index = this.getCount - 1;
      }
      return this.index;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/variables.scss';
@import './scss/tab.scss';

.disabled {
  opacity: 0.6;
}

.email-textarea::v-deep textarea {
  line-height: 1rem !important;
}

.comments {
  border: 1px solid #d8d8d8;
  border-radius: 0.25rem;
  margin: 0.25rem;
  max-height: calc(100% - 96px);
}

.comments-list {
  flex: 0 1 auto;
  max-height: calc(100% - 82px);
}

.snackbar-text {
  &::v-deep .v-snack__content {
    font-size: 18px;
    font-weight: 500;
    padding: 20px 16px;
  }
}
.new-note {
  font-size: 14px;
  color: $gray-dark;
  letter-spacing: 0.23px;
  font-weight: 600;
  padding: 8px 0 8px 14px;
  cursor: pointer;
}
.no-notes {
  &__box {
    margin-top: 114px;
  }
  &__title {
    text-align: center;
    color: #b3b4b8;
    margin-top: 18px;
    font-size: 16px;
    letter-spacing: 0;
    font-weight: 500;
  }
  &__subtitle {
    text-align: center;
    color: #b3b4b8;
    margin-top: 16px;
    font-size: 14px;
    letter-spacing: 0;
    font-weight: 400;
    padding: 0 20px 0 20px;
  }
  &__icon-box {
    width: 138px;
    height: 138px;
    border-radius: 100%;
    background-color: $title-color;
    display: grid;
    place-content: center;
    align-content: center;
  }
}
.notes-card {
  overflow-y: auto;
  height: 100%;
  &__item {
    border-bottom: 0 !important;
  }
}
.big-box {
  overflow-y: hidden;
}
.custom-note {
  height: 170px;
  border: none !important;
  &::v-deep .row-textarea {
    height: 170px !important;
    overflow-y: auto;
    padding-bottom: 0 !important;
  }
  &::v-deep textarea {
    min-height: 135px !important;
    padding-right: 2px;
  }
}
.ok-btn {
  margin-right: 20px;
  &::v-deep .v-btn__content {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0;
  }
}
.box-border {
  border: 2px solid $primary;
  padding-bottom: 7px;
  border-radius: 4px;
}
.candidate-name-box {
  font-size: 14px;
  color: $gray-dark;
  letter-spacing: 0;
  font-weight: 600;
  padding-top: 4px;
}
.notes-box {
  padding-top: 6px;
}
</style>
